import { Connector } from '@web3-react/types'
import { gnosisSafeConnection, injectedConnection, networkConnection } from 'connection'
import { useGetConnection } from 'connection'
import { Connection, ConnectionType } from 'connection/types'
import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { updateSelectedWallet } from 'state/user/reducer'

async function connect(connector: Connector) {
  try {
    if (connector.connectEagerly) {
      await connector.connectEagerly()
    } else {
      await connector.activate()
    }
  } catch (error) {
    console.debug(`web3-react eager connection error: ${error}`)
  }
}

export default function useEagerlyConnect() {
  const dispatch = useAppDispatch()

  const selectedWallet = useAppSelector((state) => state.user.selectedWallet)
  const getConnection = useGetConnection()
  const injectedIsActive = injectedConnection.hooks.useIsActive()

  let selectedConnection: Connection | undefined
  if (selectedWallet) {
    try {
      selectedConnection = getConnection(selectedWallet)
    } catch {
      dispatch(updateSelectedWallet({ wallet: undefined }))
    }
  }

  useEffect(() => {
    connect(gnosisSafeConnection.connector)
    connect(networkConnection.connector)

    if (selectedConnection) {
      connect(selectedConnection.connector)
    } // The dependency list is empty so this is only run once on mount
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedWallet !== ConnectionType.INJECTED || injectedIsActive) return

    let reconnecting = false
    const reconnect = async () => {
      if (document.visibilityState !== 'visible' || reconnecting) return
      reconnecting = true
      await connect(injectedConnection.connector)
      reconnecting = false
    }
    const reconnectWhenVisible = () => {
      if (document.visibilityState === 'visible') reconnect()
    }

    const initialRetry = window.setTimeout(reconnect, 2_000)
    const retryInterval = window.setInterval(reconnect, 15_000)
    window.addEventListener('focus', reconnect)
    document.addEventListener('visibilitychange', reconnectWhenVisible)

    return () => {
      window.clearTimeout(initialRetry)
      window.clearInterval(retryInterval)
      window.removeEventListener('focus', reconnect)
      document.removeEventListener('visibilitychange', reconnectWhenVisible)
    }
  }, [injectedIsActive, selectedWallet])
}
